import { FORM_RECORD_FLAGS, RECORD_STATUSES } from '@felix/domain';
import type { FormRecordQuery, FormRecordSort, FormRecordSortKey } from './types.js';
import { FORM_RECORD_SORT_KEYS } from './types.js';

/**
 * Form History'nin adres sorgusu (ADR 0042 §21; plan M8b Task 19, 21) [sorular #246]: süzgeç,
 * sıra ve sayfa adreste kalır; yenilemede aynı liste açılır. Ofiste gemi `?vessel=`.
 *
 * | Parametre | Süzgeç |
 * |---|---|
 * | `status` | durum |
 * | `category` | kategori (`folder:<anahtar>`) |
 * | `form` | form (belge kimliği) |
 * | `from`, `to` | kayıt günü aralığı |
 * | `vessel` | gemi (ofis) |
 * | `amended=1` | yalnız değiştirilmiş |
 * | `flagged=1` | eski revizyon işaretli |
 * | `overdue=1` | süresi geçmiş |
 * | `awaiting=me` | "Awaiting my approval" |
 * | `awaiting=upcoming` | sırası gelmemiş adımlarım (panonun "+N sırada"ı; ADR 0156 §1) |
 */

export const HISTORY_PAGE_SIZE = 25;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface HistoryFilter {
  status: FormRecordQuery['status'] | null;
  /** Kategori konusu (`folder:<anahtar>`). */
  category: string | null;
  /** Form (belge kimliği). */
  form: string | null;
  from: string | null;
  to: string | null;
  vessel: string | null;
  amended: boolean;
  flagged: boolean;
  overdue: boolean;
  awaiting: boolean;
  /** Kişiyi zincirde bekleyen, sırası gelmemiş adımlar (ADR 0069); `awaiting` ile birlikte olmaz. */
  upcoming: boolean;
  sort: FormRecordSort;
  page: number;
}

export const EMPTY_HISTORY_FILTER: HistoryFilter = {
  status: null,
  category: null,
  form: null,
  from: null,
  to: null,
  vessel: null,
  amended: false,
  flagged: false,
  overdue: false,
  awaiting: false,
  upcoming: false,
  sort: '-recordNo',
  page: 1,
};

const SORTS = new Set<string>([
  ...FORM_RECORD_SORT_KEYS,
  ...FORM_RECORD_SORT_KEYS.map((key) => `-${key}`),
]);

/** Adres sorgusundan süzgeç; geçersiz değer yok sayılır. */
export function historyFilter(search: string): HistoryFilter {
  const params = new URLSearchParams(search);
  const get = (key: string) => params.get(key);
  const status = get('status');
  const category = get('category');
  const form = get('form');
  const vessel = get('vessel');
  const day = (key: string) => {
    const value = get(key);
    return value && DAY.test(value) ? value : null;
  };
  const sort = get('sort');
  const page = Number(get('page'));
  return {
    status:
      status && (RECORD_STATUSES as readonly string[]).includes(status)
        ? (status as HistoryFilter['status'])
        : null,
    category: category?.startsWith('folder:') ? category : null,
    form: form && UUID.test(form) ? form : null,
    from: day('from'),
    to: day('to'),
    vessel: vessel && UUID.test(vessel) ? vessel : null,
    amended: get('amended') === '1',
    flagged: get('flagged') === '1',
    overdue: get('overdue') === '1',
    awaiting: get('awaiting') === 'me',
    upcoming: get('awaiting') === 'upcoming',
    sort: sort && SORTS.has(sort) ? (sort as FormRecordSort) : '-recordNo',
    page: Number.isInteger(page) && page >= 1 ? page : 1,
  };
}

/** Süzgeçten adres sorgusu (varsayılanlar yazılmaz). */
export function historySearch(filter: HistoryFilter): string {
  const params = new URLSearchParams();
  if (filter.status) params.set('status', filter.status);
  if (filter.category) params.set('category', filter.category);
  if (filter.form) params.set('form', filter.form);
  if (filter.from) params.set('from', filter.from);
  if (filter.to) params.set('to', filter.to);
  if (filter.vessel) params.set('vessel', filter.vessel);
  if (filter.amended) params.set('amended', '1');
  if (filter.flagged) params.set('flagged', '1');
  if (filter.overdue) params.set('overdue', '1');
  if (filter.awaiting) params.set('awaiting', 'me');
  else if (filter.upcoming) params.set('awaiting', 'upcoming');
  if (filter.sort !== '-recordNo') params.set('sort', filter.sort);
  if (filter.page > 1) params.set('page', String(filter.page));
  const text = params.toString();
  return text ? `?${text}` : '';
}

/** Süzgeçten sunucu sorgusu (gemi ve ofis uçları; ADR 0042 Ek U26). */
export function historyQuery(filter: HistoryFilter): FormRecordQuery {
  const subject = filter.form ? `document:${filter.form}` : filter.category;
  return {
    ...(filter.status ? { status: filter.status } : {}),
    ...(subject ? { subject } : {}),
    ...(filter.from ? { from: filter.from } : {}),
    ...(filter.to ? { to: filter.to } : {}),
    ...(filter.vessel ? { vesselId: filter.vessel } : {}),
    ...(filter.amended ? { amended: true } : {}),
    ...(filter.flagged ? { flagged: true } : {}),
    ...(filter.overdue ? { overdue: true } : {}),
    ...(filter.awaiting
      ? { awaiting: 'me' as const }
      : filter.upcoming
        ? { awaiting: 'upcoming' as const }
        : {}),
    sort: filter.sort,
    page: filter.page,
    pageSize: HISTORY_PAGE_SIZE,
  };
}

/** Süzgeçlerden biri seçili mi ("Clear filters" ve "eşleşme yok"). */
export function historyFiltered(filter: HistoryFilter): boolean {
  return Boolean(
    filter.status ||
      filter.category ||
      filter.form ||
      filter.from ||
      filter.to ||
      filter.vessel ||
      filter.amended ||
      filter.flagged ||
      filter.overdue ||
      filter.awaiting ||
      filter.upcoming,
  );
}

/** Başlığa tıklanınca sıra: aynı sütunda yön döner, başka sütunda artan başlar. */
export function nextHistorySort(
  current: FormRecordSort,
  column: FormRecordSortKey,
): FormRecordSort {
  if (current === column) return `-${column}`;
  if (current === `-${column}`) return column;
  return column;
}

export const HISTORY_FLAGS = FORM_RECORD_FLAGS;
